import { useState, ReactNode, CSSProperties, MouseEvent } from 'react';
import { useTheme } from '@emotion/react';
import Loader from './Loader';
import Icon from 'src/components/Icon';

interface UWETheme {
  primaryMain: string;
  successMain: string;
  warningMain: string;
  dangerMain: string;
  infoMain: string;
  [key: string]: string;
}

export type TileButtonColorKey = 'primaryMain' | 'successMain' | 'warningMain' | 'dangerMain' | 'infoMain';

export type TileButtonSize = 'sm' | 'lg';

export type TileButtonDirection = 'row' | 'column';

interface SizeConfig {
  iconSize: string;
  labelSize: string;
  labelWeight: string;
  gap: string;
  padding: string;
  lineHeight: number | undefined;
}

const SIZES: Record<TileButtonSize, SizeConfig> = {
  sm: { iconSize: '22px', labelSize: '12px', labelWeight: '600', gap: '8px', padding: '12px 8px', lineHeight: 1.3 },
  lg: { iconSize: '36px', labelSize: '18px', labelWeight: '700', gap: '10px', padding: '24px 20px 20px', lineHeight: undefined },
};

export interface TileButtonProps {
  icon: string;
  label: ReactNode;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  colorKey?: TileButtonColorKey;
  size?: TileButtonSize;
  active?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Fixed width. Omit to use flex: 1 (fills available space). */
  width?: number | string;
  /** Fixed height. */
  height?: number | string;
  /** Layout of icon and label. Defaults to 'column' (icon above label). */
  direction?: TileButtonDirection;
  /** Overrides the size-based padding. */
  padding?: string;
  className?: string;
  children?: ReactNode;
}

function TileButton({
  colorKey = 'primaryMain',
  icon,
  label,
  active,
  disabled,
  loading,
  onClick,
  width,
  height,
  direction = 'column',
  padding,
  size = 'sm',
  className,
  children,
}: TileButtonProps) {
  const theme = (useTheme() as unknown) as UWETheme;
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const main = theme[colorKey];
  const { iconSize, labelSize, labelWeight, gap, padding: sizePadding, lineHeight } = SIZES[size];

  const isFixed = width !== undefined || height !== undefined;
  const isInteractive = !disabled && !loading;

  const style: CSSProperties = {
    ...(isFixed ? { width, height } : { flex: 1 }),
    display: 'flex',
    flexDirection: direction,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap,
    padding: padding ?? sizePadding,
    border: `2px solid ${active || hovered ? main : `${main}99`}`,
    borderRadius: '12px',
    background: active ? `${main}30` : hovered ? `${main}20` : `${main}0d`,
    cursor: isInteractive ? 'pointer' : 'default',
    transition: 'border-color 150ms ease, background 150ms ease, box-shadow 150ms ease, transform 100ms ease',
    boxShadow: hovered && isInteractive ? '0 4px 8px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.06)',
    transform: pressed && isInteractive ? 'translateY(0)' : hovered && isInteractive ? 'translateY(-2px)' : 'translateY(0)',
    userSelect: 'none',
    opacity: disabled ? 0.5 : 1,
    minWidth: 0,
  };

  return (
    <div
      className={className}
      style={style}
      onClick={isInteractive ? onClick : undefined}
      onMouseEnter={() => isInteractive && setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => isInteractive && setPressed(true)}
      onMouseUp={() => setPressed(false)}
    >
      {loading ? <Loader /> : <Icon icon={icon} fontSize={iconSize} color={main} />}
      <span style={{ fontSize: labelSize, fontWeight: labelWeight, color: main, lineHeight }}>{label}</span>
      {children}
    </div>
  );
}

export default TileButton;
